//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2025, 2026. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

import React from 'react';
import { useTranslation } from 'react-i18next';
import LandingMiniNav from './LandingMiniNav';
import withZoom from '../Zoom/withZoom';
import { useTeamspaceContext } from '../../TeamspaceContext';
import { useNavigate } from 'react-router-dom';
import storageService from '../../utils/storageService';
import { RawUser } from './Landing';

interface Step {
  id: number;
  name: string;
}

interface RightSectionProps {
  mode: string;
  loopName: string;
  step: number;
  steps: Step[];
  images: string[];
  pages: React.ReactNode[];
  headings: string[];
  progress: number;
  handleNextStep: () => void;
  handleBackStep: () => void;
  handleCreate: () => void;
  handleHome: () => void;
  handleRetryTeamspace: () => void;
  handleRetryLaterTeamspace: () => void;
  handleRetryLoop: () => void;
  handleRetryLaterLoop: () => void;
  showSuccess: boolean;
  isZoomedIn?: boolean;
  errMsgSuccPage: string;
  teamspaceId: string | undefined;
  warning?: string;
  defineError?: string;
  selectedColor?: string;
  selectedMembers?: RawUser[];
}

const RightSection: React.FC<RightSectionProps> = ({
  mode,
  loopName,
  step,
  steps,
  pages,
  headings,
  handleNextStep,
  handleBackStep,
  handleCreate,
  handleHome,
  handleRetryTeamspace,
  handleRetryLaterTeamspace,
  handleRetryLoop,
  handleRetryLaterLoop,
  showSuccess,
  isZoomedIn,
  errMsgSuccPage,
  teamspaceId,
  warning,
  defineError,
  selectedColor,
  selectedMembers,
}) => {
  const { t } = useTranslation();

  const MEMBER_COLORS = [
    '#E91E63',
    '#9C27B0',
    '#3F51B5',
    '#2196F3',
    '#00BCD4',
    '#4CAF50',
  ];

  const getInitials = (name: string) => {
    if (!name) return 'TS';
    return name
      .split(' ')
      .map((w) => w[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getMemberInitials = (member: RawUser) => {
    if (member.firstname && member.lastname) {
      return (member.firstname[0] + member.lastname[0]).toUpperCase();
    }
    if (member.fullname) {
      return member.fullname
        .split(' ')
        .map((w) => w[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
    }
    return (member.username?.[0] ?? member.email[0]).toUpperCase();
  };
  const {
    _setTeamspaceId,
    _setTeamspaceName,
    _setTeamspaceSlug,
    teamspaceList,
  } = useTeamspaceContext();
  const navigate = useNavigate();
  let headingText: string;
  if (showSuccess) {
    if (mode === 'teamspace') {
      headingText = t('teamspace-is-ready');
    } else {
      headingText = t('loop-is-ready');
    }
  } else {
    headingText = headings[step - 1];
  }
  const goToTeamspace = () => {
    if (mode === 'teamspace') {
      _setTeamspaceId(teamspaceId);
      _setTeamspaceName(loopName);
      // Get slug from teamspace list (comes from backend)
      const teamspace = teamspaceList?.find((ts) => ts.id === teamspaceId);
      _setTeamspaceSlug(teamspace?.slug);
      storageService.setCurrentTeamspace(teamspaceId, loopName);
    }

    navigate('/');
  };
  const nextBtnDisabled = !loopName.trim() || !!warning || !!defineError;
  const isLastStep = step === steps.length;
  const showHeading = !isLastStep && !(mode === 'teamspace' && step === 1);

  return (
    <div
      id="rightSection"
      data-testid="right-section"
      className="flex w-full flex-col"
    >
      <form id="form" data-testid="form" className="flex w-full flex-col">
        {step <= steps.length && <LandingMiniNav step={step} steps={steps} />}

        {showHeading && (
          <p
            id="loopHeading"
            data-testid="loop-heading"
            className="m-0 mb-4 font-inter text-xl font-semibold"
          >
            {headingText}
          </p>
        )}

        <div
          id="id-create-right-content"
          data-testid="create-right-content"
          className="flex-1 overflow-auto"
        >
          {isLastStep && mode === 'teamspace' ? (
            <div
              id="id-create-right-teamspace-success"
              data-testid="create-right-teamspace-success"
              className="px-4 py-8 text-center"
            >
              <div
                id="id-create-right-success-icon"
                data-testid="create-right-success-icon"
                className="mb-6"
              >
                <svg
                  width="80"
                  height="80"
                  viewBox="0 0 80 80"
                  fill="none"
                  className="mx-auto"
                >
                  <circle cx="40" cy="40" r="40" fill="#24a148" opacity="0.1" />
                  <circle cx="40" cy="40" r="30" fill="#24a148" />
                  <path
                    d="M25 40L35 50L55 30"
                    stroke="white"
                    strokeWidth="4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <h2 className="mb-4 text-2xl font-normal text-carbon-dark dark:text-white">
                {t('teamspace-created-successfully')}
              </h2>
              <p className="mx-auto mb-8 max-w-md text-base text-carbon-textSecondary dark:text-brand-disabled">
                {t('teamspace-created-description', { name: loopName })}
              </p>
              <div
                id="id-create-right-details-card"
                data-testid="create-right-details-card"
                className="mx-auto max-w-md space-y-3 rounded-lg bg-white p-4 text-left dark:bg-carbon-darkPanel"
              >
                <div
                  id="id-create-right-name-row"
                  data-testid="create-right-name-row"
                  className="flex items-center gap-3"
                >
                  <div
                    id="id-create-right-initials"
                    data-testid="create-right-initials"
                    className="flex size-10 shrink-0 items-center justify-center rounded-full bg-brand-blue text-sm font-semibold text-white"
                    style={
                      selectedColor
                        ? { backgroundColor: selectedColor }
                        : undefined
                    }
                  >
                    {getInitials(loopName)}
                  </div>
                  <p className="font-medium text-gray-800 dark:text-gray-100">
                    {loopName}
                  </p>
                </div>
                {selectedMembers && selectedMembers.length > 0 && (
                  <div
                    id="id-create-right-members-section"
                    data-testid="create-right-members-section"
                    className="border-t border-gray-200 pt-3 dark:border-gray-600"
                  >
                    <p className="mb-2 text-sm text-[#0f62fe] dark:text-[#78a9ff]">
                      {selectedMembers.length}{' '}
                      {selectedMembers.length === 1
                        ? t('member-added')
                        : t('members-added')}
                    </p>
                    <div
                      id="id-create-right-members-list"
                      data-testid="create-right-members-list"
                      className="space-y-2"
                    >
                      {selectedMembers.slice(0, 3).map((member, idx) => (
                        <div
                          key={member.id || String(idx)}
                          id={`id-create-right-member-${idx}`}
                          data-testid={`create-right-member-${idx}`}
                          className="flex items-center gap-2"
                        >
                          <div
                            id={`id-create-right-member-initials-${idx}`}
                            data-testid={`create-right-member-initials-${idx}`}
                            className="flex size-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold text-white"
                            style={{
                              backgroundColor:
                                MEMBER_COLORS[idx % MEMBER_COLORS.length],
                            }}
                          >
                            {getMemberInitials(member)}
                          </div>
                          <span
                            id={`id-create-right-member-name-${idx}`}
                            data-testid={`create-right-member-name-${idx}`}
                            className="text-xs text-gray-600 dark:text-gray-400"
                          >
                            {member.fullname || member.username || member.email}
                          </span>
                        </div>
                      ))}
                      {selectedMembers.length > 3 && (
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          +{selectedMembers.length - 3} more
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : isLastStep ? (
            <div
              id="id-create-right-loop-success"
              data-testid="create-right-loop-success"
              className="px-4 py-8 text-center"
            >
              <div
                id="id-create-right-loop-success-icon"
                data-testid="create-right-loop-success-icon"
                className="mb-6"
              >
                <svg
                  width="80"
                  height="80"
                  viewBox="0 0 80 80"
                  fill="none"
                  className="mx-auto"
                >
                  <circle cx="40" cy="40" r="40" fill="#24a148" opacity="0.1" />
                  <circle cx="40" cy="40" r="30" fill="#24a148" />
                  <path
                    d="M25 40L35 50L55 30"
                    stroke="white"
                    strokeWidth="4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <h2 className="mb-4 text-2xl font-normal text-carbon-dark dark:text-white">
                {t('loop-created-successfully')}
              </h2>
              <p className="mx-auto mb-8 max-w-md text-base text-carbon-textSecondary dark:text-brand-disabled">
                {t('loop-created-description', { name: loopName })}
              </p>
            </div>
          ) : (
            pages[step - 1] ?? null
          )}
        </div>

        {/* Teamspace mode buttons */}
        {mode === 'teamspace' && step < steps.length - 1 && (
          <div
            id="button-div"
            data-testid="button-div"
            className="m-4 mt-6 flex pt-4"
          >
            <button
              id="id-cancel-btn"
              data-testid="cancel-btn"
              type="button"
              onClick={handleHome}
              className="min-w-[80px] cursor-pointer rounded border border-carbon-textDisabled bg-white px-4 py-2 text-sm text-carbon-dark"
            >
              {t('cancel')}
            </button>
            {step === steps.length - 2 ? (
              <div
                id="id-create-right-action-buttons"
                data-testid="create-right-action-buttons"
                className="ml-auto flex gap-2"
              >
                <button
                  id="id-back-btn"
                  data-testid="back-btn"
                  type="button"
                  onClick={handleBackStep}
                  className="min-w-[80px] cursor-pointer rounded border border-carbon-textDisabled bg-white px-4 py-2 text-sm text-carbon-dark"
                >
                  {t('Back')}
                </button>
                <button
                  id="id-create-btn"
                  data-testid="create-btn"
                  type="button"
                  onClick={handleCreate}
                  className="min-w-[80px] cursor-pointer rounded border-0 bg-brand-blue px-4 py-2 text-sm text-white"
                >
                  {t('create-teamspace')}
                </button>
              </div>
            ) : (
              <button
                id="id-next-btn"
                data-testid="next-btn"
                type="button"
                onClick={handleNextStep}
                disabled={nextBtnDisabled}
                className={`ml-auto min-w-[80px] rounded border-0 px-4 py-2 text-sm text-white ${nextBtnDisabled ? 'cursor-not-allowed bg-brand-disabled' : 'cursor-pointer bg-brand-blue'}`}
              >
                {t('Next')}
              </button>
            )}
          </div>
        )}

        {mode === 'teamspace' &&
          step === steps.length - 1 &&
          errMsgSuccPage.length === 0 && (
            <div
              id="button-div"
              data-testid="button-div"
              className="m-4 mt-6 flex pt-4"
            >
              <button
                id="id-cancel-btn"
                data-testid="cancel-btn"
                type="button"
                onClick={handleHome}
                className="min-w-[80px] cursor-pointer rounded border border-carbon-textDisabled bg-white px-4 py-2 text-sm text-carbon-dark"
              >
                {t('cancel')}
              </button>
            </div>
          )}

        {/* Retry buttons on the creation step (step 3) when creation fails */}
        {step === steps.length - 1 &&
          errMsgSuccPage.length > 0 &&
          !showSuccess && (
            <div
              id="button-div"
              data-testid="button-div"
              className="mt-6 flex justify-between border-t border-carbon-border pt-4"
            >
              <button
                id="id-retry-later-button"
                data-testid="id-retry-later-button"
                type="button"
                onClick={
                  mode === 'teamspace'
                    ? handleRetryLaterTeamspace
                    : handleRetryLaterLoop
                }
                className="min-w-[80px] cursor-pointer rounded border border-carbon-textDisabled bg-white px-4 py-2 text-sm text-carbon-dark"
              >
                {t('retry-later')}
              </button>
              <button
                id="id-retry-now-button"
                data-testid="id-retry-now-button"
                type="button"
                onClick={
                  mode === 'teamspace' ? handleRetryTeamspace : handleRetryLoop
                }
                className={`min-w-[80px] cursor-pointer rounded px-4 py-2 text-sm ${
                  mode === 'teamspace'
                    ? 'border border-brand-blue bg-white text-brand-blue'
                    : 'border-0 bg-brand-blue text-white'
                }`}
              >
                {t('retry-now')}
              </button>
            </div>
          )}

        {/* Loop mode buttons */}
        {mode !== 'teamspace' && step === 1 && (
          <div
            id="button-div"
            data-testid="button-div"
            className="m-4 mt-6 flex pt-4"
          >
            <button
              id="id-cancel-btn"
              data-testid="cancel-btn"
              type="button"
              onClick={handleHome}
              className="min-w-[80px] cursor-pointer rounded border border-carbon-textDisabled bg-white px-4 py-2 text-sm text-carbon-dark"
            >
              {t('cancel')}
            </button>
            <button
              id="id-next-btn"
              data-testid="next-btn"
              type="button"
              onClick={handleNextStep}
              disabled={nextBtnDisabled}
              className={`ml-auto min-w-[80px] rounded border-0 px-4 py-2 text-sm text-white ${nextBtnDisabled ? 'cursor-not-allowed bg-brand-disabled' : 'cursor-pointer bg-brand-blue'}`}
            >
              {t('Next')}
            </button>
          </div>
        )}

        {mode !== 'teamspace' && step > 1 && step < steps.length - 1 && (
          <div
            id="button-div"
            data-testid="button-div"
            className="m-4 mt-6 flex pt-4"
          >
            <button
              id="id-cancel-btn"
              data-testid="cancel-btn"
              type="button"
              onClick={handleHome}
              className="min-w-[80px] cursor-pointer rounded border border-carbon-textDisabled bg-white px-4 py-2 text-sm text-carbon-dark"
            >
              {t('cancel')}
            </button>
            <div
              id="id-create-right-action-buttons"
              data-testid="create-right-action-buttons"
              className="ml-auto flex gap-2"
            >
              <button
                id="id-back-btn"
                data-testid="back-btn"
                type="button"
                onClick={handleBackStep}
                className="min-w-[80px] cursor-pointer rounded border border-carbon-textDisabled bg-white px-4 py-2 text-sm text-carbon-dark"
              >
                {t('Back')}
              </button>
              <button
                id="id-next-btn"
                data-testid="next-btn"
                type="button"
                onClick={
                  step === steps.length - 2 ? handleCreate : handleNextStep
                }
                className="min-w-[80px] cursor-pointer rounded border-0 bg-brand-blue px-4 py-2 text-sm text-white"
              >
                {t('Next')}
              </button>
            </div>
          </div>
        )}

        {isLastStep && showSuccess && (
          <div
            id="id-create-right-exit-wrapper"
            data-testid="create-right-exit-wrapper"
            className="mt-6 flex justify-end border-t border-carbon-border pt-4"
          >
            <button
              id="id-exit-btn"
              data-testid="exit-btn"
              type="button"
              onClick={goToTeamspace}
              className="min-w-[80px] cursor-pointer rounded border-0 bg-brand-blue px-4 py-2 text-sm text-white"
            >
              {mode === 'teamspace' ? t('go-to-dashboard') : t('go-to-my-loop')}
            </button>
          </div>
        )}
      </form>
    </div>
  );
};

export default withZoom(RightSection, 200);
